import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/deer-blind-hunting-welded-steel-seguin-tx.jpg";
import heroPicture from "@/assets/deer-blind-hunting-welded-steel-seguin-tx.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import { HuntingTarpForm } from "@/components/HuntingTarpForm";

export const Route = createFileRoute("/deer-blinds-and-hunting")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Deer Blinds & Hunting Builds in Texas — Welded Tower Blinds, Hunting Rigs | Seguin, TX",
      description:
        "Custom welded deer blinds and complete hunting builds in Seguin, TX — tower blinds, tripod stands, box blinds, feeder pens.",
      image: hero,
      imageAlt:
        "Welded steel insulated deer blind on tall tripod legs in a South Texas hunting lease at golden hour with a hunting truck",
      socialTitle: "Deer Blinds & Hunting Builds — Welded in Seguin, TX",
      socialDescription:
        "Tower blinds, tripod stands, box blinds, feeder pens and complete hunting truck packages — welded aluminum or steel, built lease-tough in our Seguin fabrication shop.",
      keywords:
        "deer blind Texas, custom deer blinds, welded deer blind, tower deer blind, tripod deer stand, box blind, insulated deer blind, shooting house, feeder pen, hunting blind Seguin TX, South Texas deer blind, Hill Country deer blind, custom hunting rig, hunting truck builds",
    }),
    scripts: serviceHeadScripts({
      path: "/deer-blinds-and-hunting",
      serviceName: "Deer Blinds & Hunting Builds",
      breadcrumbName: "Deer Blinds & Hunting",
      description:
        "Custom deer blinds and hunting builds fabricated in Seguin, TX — tower blinds, tripod stands, box blinds, shooting houses.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/deer-blinds-and-hunting"
      serviceName="Deer Blinds & Hunting Builds"
      breadcrumbs={[
        { label: "Welding & Custom Fabrication", to: "/custom-fabrication" },
        { label: "Deer Blinds & Hunting" },
      ]}
      eyebrow="★ DEER BLINDS & HUNTING ★"
      title="Custom Deer Blinds, Tower Stands, Box Blinds & Hunting Rigs"
      intro="From a single tripod stand on a back forty to a fully insulated tower blind on a managed lease — we weld deer blinds and complete hunting builds to order in our Seguin, TX shop. Aluminum or steel, sized to your lease, your truck, and how you hunt."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Welded Aluminum or Steel · Seguin, TX",
        headline: "Planning a Deer Blind or Hunting Build? Get a Free Written Quote.",
        subhead:
          "Tell us what you need — tower blind, tripod, box blind, shooting house, feeder pen or full hunting truck package — with sizes, window style and finish. We'll come back same business day.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Deer Blind / Hunting Build")}&notes=${encodeURIComponent("Deer blind / hunting build inquiry — please describe type (tower blind, tripod stand, box blind, shooting house, feeder pen, hunting truck), size (LxWxH), tower height, window style (drop / sliding / horizontal slit), material (aluminum or steel), and finish (powder coat color or camo).")}`,
        primaryLabel: "Get My Deer Blind Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Tower blinds at 5', 10', 15' and 20'+ on welded steel or aluminum legs",
        "Skid-mounted blinds you can drag with a tractor, truck or side-by-side",
        "Trailer-mounted hunting rigs with bunks, AC, solar and storage",
        "Insulated 'Redneck-style' luxury blinds — tinted windows, carpet, swivel chairs",
        "Bow blinds with silent hinge windows, dark interiors and low-profile heights",
        "Common sizes 4x4, 4x6, 5x5, 6x6 — or fully custom to your lease",
        "Turn-key ranch packages: blind + feeder + protein + water + solar + camera",
      ]}
      body={[
        {
          heading: "Tower Deer Blinds — The Texas Standard",
          text:
            "The classic elevated blinds you see across Texas ranch land. We weld towers in steel or aluminum at 5', 10', 15' and 20'+ heights, with full-height enclosed houses on top. Elevation gives you better visibility, a wider shooting range, improved scent control and safer shooting angles over feeders and senderos.",
          bullets: [
            "Tower options — steel towers, aluminum towers, wood legs or skid platforms",
            "Heights from 5' youth blinds up to 20'+ for South Texas brush country",
            "Most popular box sizes in Texas — 4x6 and 5x5, with 4x4 and 6x6 also common",
            "Insulated walls and roof, carpeted floors, swivel chairs and shooting rests",
            "Powder coat or camo finishes — color matched to your lease",
          ],
          cta: { label: "See Custom Welding & Fabrication", href: "/custom-fabrication" },
        },
        {
          heading: "Skid-Mounted Deer Blinds — Move With the Deer",
          text:
            "The most popular custom blind style in Texas right now. Built on heavy welded skids so you can drag them with a tractor, truck or side-by-side as feeders move, deer patterns shift, or you rotate hunters across the lease.",
          bullets: [
            "Heavy-duty welded skids sized for tractor or truck tow",
            "Reinforced tow points and lift eyes",
            "Same insulated box and window options as tower blinds",
            "Perfect for ranch rotations and lease hunting",
          ],
        },
        {
          heading: "Trailer-Mounted Hunting Rigs",
          text:
            "Welded onto a utility trailer for serious mobility — lease hunting, large ranch operations, or anyone moving between properties. Some are basically mini hunting cabins.",
          bullets: [
            "Fully enclosed with bunks, AC compatibility and storage",
            "Solar-ready electrical packages",
            "Removable shooting rests and gear racks",
            "Tie-downs, lockable doors and weather sealing",
          ],
        },
        {
          heading: "Bow Hunting Blinds",
          text:
            "Built specifically for archery — narrow shooting windows, dark interiors, silent windows and a low-profile height so deer don't pattern the box.",
          bullets: [
            "Vertical guillotine and silent hinge windows for clean draws",
            "Dark interior paint and carpet to hide movement",
            "Low-profile height and ground-level options",
            "Bow hangers, gear hooks and quiet floor materials",
          ],
        },
        {
          heading: "'Redneck-Style' Luxury Blinds — The Premium Tier",
          text:
            "High-end insulated blinds that function almost like a hunting office. This is where the premium market lives in Texas right now — buyers spend on comfort, insulation and tech because they live in these boxes for entire weekends and seasons.",
          bullets: [
            "Insulated walls, ceiling and floor for sound dampening and Texas heat",
            "Tinted plexiglass windows with silent hinges or sliders",
            "Swivel chairs, padded shooting rests, marine carpet flooring",
            "Solar power, LED lighting, USB charging and AC / heater compatibility",
            "Lockable doors, cup holders and storage shelves",
          ],
        },
        {
          heading: "Ground Blinds & Pop-Ups",
          text:
            "Lightweight, portable and cheap to set — best for bow hunters, temporary setups and wooded areas where elevation isn't an option. We carry these for hunters who want a movable blind that pairs with their custom tower or skid blind back at camp.",
          bullets: [
            "Lightweight fabric and pop-up frames",
            "Easy concealment in brush and wooded cover",
            "Best paired with a permanent tower or skid blind elsewhere on the lease",
            "Limitations — lower visibility, more scent exposure, less airflow in Texas heat",
          ],
        },
        {
          heading: "Feeder Pens, Protein Cages & Turn-Key Ranch Packages",
          text:
            "More Texas customers want everything in one order — blind, feeder, protein, water setup, camera mounts, solar and storage built and delivered together. We weld feeder pens and stands sized to whatever you run, and we'll spec the full turn-key package against the rest of your build.",
          bullets: [
            "Hog-proof panel pens around protein feeders",
            "Welded leg stands for 300 lb to 1,000+ lb feeders",
            "Spin plate covers, varmint guards, water tank mounts",
            "Combo feeder + blind packages built around the same sendero",
            "Solar-ready electrical and camera mount kits",
          ],
          cta: { label: "See Custom Dog Boxes & Hunting Trucks", href: "/dog-boxes" },
        },
        {
          heading: "Materials — Aluminum, Steel & Wood",
          text:
            "Each material has a place. We'll match the build to how the blind will live — permanent tower, mobile skid, trailer rig or DIY.",
          bullets: [
            "Aluminum — fast-growing in Texas. Lightweight, rust resistant, easier to transport, premium appearance. Best for mobile blinds, trailer setups and welded custom fabrication.",
            "Steel — durable, secure and long-lasting. Heavier and hotter in Texas heat, but the right call for permanent towers, skids and frames.",
            "Wood — traditional and affordable for DIY ranch blinds, but expect higher maintenance, rot and insect issues over time.",
          ],
        },
        {
          heading: "Roof Styles & Window Types",
          text:
            "Two of the details that separate a premium blind from a cheap one. We'll spec roof and windows to your hunting style and weather.",
          bullets: [
            "Flat roof — simple and affordable",
            "Sloped roof — better drainage and cooling",
            "Overhang roof — Texas favorite; shades windows, reduces heat and keeps rain off openings",
            "Horizontal slider windows — classic rifle setup",
            "Vertical guillotine windows — clean draw for bow hunters",
            "Silent hinge windows — minimal noise on the open",
            "Tinted plexiglass — hides movement inside the box",
            "Rifle rest windows — integrated shooting rests on the sill",
          ],
        },
        {
          heading: "Features Texas Hunters Ask For Most",
          text:
            "Most builds bundle several of these. Tell us which matter most and we'll spec the rest of the blind around them.",
          bullets: [
            "Insulated walls, quiet and dark interiors",
            "Solar-powered fans and AC compatibility",
            "Storage shelves, gun rests, cup holders",
            "Tinted windows and lockable doors",
            "Weather sealing and marine carpet flooring",
            "Camo or color-matched powder coat finishes",
            "USB charging, LED interior lighting and solar packages",
            "Ramp doors and ground-access kits for youth and disabled hunters",
          ],
        },
      ]}
      applicationsEyebrow="WHO WE BUILD FOR"
      applicationsHeading="Built For Texas Hunters, Outfitters & Ranchers"
      applications={[
        "Family Deer Leases",
        "South Texas Trophy Ranches",
        "Hill Country Hunting Camps",
        "Hunting Outfitters & Guides",
        "Youth & Disabled Hunter Programs",
        "Working Cattle & Hunting Ranches",
      ]}
      faqs={[
        {
          q: "Do you build deer blinds in aluminum or steel?",
          a: "Both. Aluminum is lighter and rust resistant — a good call for tall tower blinds you'll lift and move. Steel is heavier but cheaper and stronger — the right pick for permanent towers, feeder pens, and builds that live outside year-round. We can quote both side by side.",
        },
        {
          q: "How tall can the tower legs be?",
          a: "We weld tower blinds with leg heights from about 5' for kids and youth blinds up to 12'+ for South Texas brush country. We'll size and brace the legs to the terrain and how the blind is anchored.",
        },
        {
          q: "Can you build a bow-friendly blind?",
          a: "Yes — bow blinds use vertical slit windows (or a combination of horizontal and vertical) so the arrow has clearance. Floors and walls are carpeted to cut noise, and we can add bow hangers and shelves inside.",
        },
        {
          q: "Do you build feeder pens too?",
          a: "Yes. We weld hog-proof panel pens around protein feeders, plus welded leg stands sized to whatever feeder you run — 300 lb to 1,000+ lb. We can also add spin plate covers and varmint guards.",
        },
        {
          q: "Can you finish the blind to match my lease?",
          a: "Absolutely. We powder coat in standard colors or camo patterns, and we can color-match to existing blinds and gates on your property.",
        },
        {
          q: "Can you build a complete hunting truck instead of a blind?",
          a: "Yes — dog box, hog cage, gun rack, storage, water system, lighting and finish, all welded onto one truck in our Seguin shop. Most of our hog and deer hunting customers end up there. See our Custom Dog Boxes & Hunting Trucks page for details.",
        },
      ]}
    >
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Request a Quote</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Tell Us About Your Deer Blind or Hunting Build
          </h2>
          <p className="mt-3 text-muted-foreground">
            Share the type (tower blind, tripod, box blind, shooting house, feeder pen or full hunting truck),
            dimensions, window style and finish — we'll come back with a written estimate.{" "}
            <Link to="/custom-fabrication" className="font-semibold text-navy-deep underline">
              Or see our full welding & fabrication lineup →
            </Link>
          </p>
          <div className="mt-8">
            <HuntingTarpForm />
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
